/* AresAutocomplete — Live IČO/firma search dropdown that calls /api/ares/lookup.
 *
 * Usage:
 *   <AresAutocomplete
 *     value={form.company || form.ico}
 *     onChange={(v) => setForm({ ...form, company: v })}
 *     onPick={(entity) => {
 *       // fills name, ico, legal_form, street, city, psc, region
 *     }}
 *   />
 *
 * Behavior:
 *  - 350ms debounce
 *  - searches if input is >=2 chars OR all-digits 6-8 chars (= IČO)
 *  - shows up to 10 results
 *  - clicking a result calls onPick with the normalized entity
 */
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Search, Loader2, Building2, User as UserIcon } from "lucide-react";
import { odebirejZemi, zemeTenanta } from "@/lib/tenantMena";

/* V prohlížeči relativně (/api → proxy / ingress), stejně jako lib/api.js. Absolutní
 * REACT_APP_BACKEND_URL tu dřív dělal cross-origin volání: lokálně CORS blok
 * (přílohy u kandidáta v Náboru), na mobilu riziko neposlaných cookies. 24. 10. 2026 */
const API = typeof window !== "undefined" ? "" : (process.env.REACT_APP_BACKEND_URL || "");

export default function AresAutocomplete({ value, onChange, onPick, testid, placeholder = "Hledat firmu nebo IČO v ARES…", inputClassName, inputStyle, autoFocus = false }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const ctrlRef = useRef(null);
  const debounceRef = useRef(null);
  /* ARES je ČESKÝ rejstřík — slovenskému poradci by našeptával české firmy
   * a „doplnil“ cizí IČO (rozhodnutí majitele 19. 10. 2026, CRM-SK-17).
   * U jiné země tenanta je to obyčejné textové pole. */
  const zeme = useSyncExternalStore(odebirejZemi, zemeTenanta, zemeTenanta);
  const aresZapnuty = zeme === "CZ";
  const ph = aresZapnuty || !/ARES/.test(String(placeholder || "")) ? placeholder : "IČO / firma…";

  // Debounced search
  useEffect(() => {
    if (!focused || !aresZapnuty) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = (value || "").trim();
    if (q.length < 2) {
      setResults([]); setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      // Cancel previous request
      if (ctrlRef.current) ctrlRef.current.abort();
      const ctrl = new AbortController();
      ctrlRef.current = ctrl;

      setLoading(true);
      try {
        const r = await fetch(`${API}/api/ares/lookup`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query: q }),
          signal: ctrl.signal,
        });
        if (!r.ok) { setResults([]); setOpen(false); return; }
        const data = await r.json();
        const list = data.results || [];
        setResults(list);
        setOpen(list.length > 0);
      } catch (e) {
        if (e.name !== "AbortError") { setResults([]); setOpen(false); }
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value, focused, aresZapnuty]);

  const handlePick = (entity) => {
    setOpen(false);
    setResults([]);
    onPick?.(entity);
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          type="text"
          autoFocus={autoFocus}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); setTimeout(() => setOpen(false), 200); }}
          placeholder={ph}
          data-testid={testid}
          className={inputClassName || "w-full rounded px-2 py-1 pr-7 text-maly outline-none transition-colors"}
          style={inputStyle || {
            background: "var(--surface)",
            border: "1px solid #D4C5B0",
            color: "var(--ink)",
            height: 28,
          }}
        />
        <div className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none">
          {loading
            ? <Loader2 size={12} className="animate-spin" style={{ color: "var(--ink-muted)" }} />
            : <Search size={12} style={{ color: "var(--ink-muted)" }} />}
        </div>
      </div>

      {open && results.length > 0 && (
        <div
          className="absolute z-50 left-0 right-0 mt-0.5 rounded-md shadow-lg overflow-hidden"
          style={{ background: "var(--surface)", border: "1px solid var(--border)", maxHeight: 320, overflowY: "auto" }}
          data-testid="ares-results"
        >
          {results.slice(0, 10).map((r) => {
            const Icon = r.is_person ? UserIcon : Building2;
            return (
              <button
                key={r.ico}
                onMouseDown={(e) => e.preventDefault()} // prevent blur before click
                onClick={() => handlePick(r)}
                className="w-full flex items-start gap-2 px-2.5 py-1.5 text-left hover:bg-[#FBF5EC] transition-colors border-b last:border-b-0"
                style={{ borderColor: "var(--border)" }}
                data-testid={`ares-result-${r.ico}`}
              >
                <Icon size={13} className="mt-0.5 flex-shrink-0" style={{ color: r.is_person ? "#7B3FA8" : "var(--accent)" }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold truncate" style={{ color: "var(--ink)" }}>
                    {r.name}
                  </div>
                  <div className="text-drobny flex items-center gap-1.5" style={{ color: "var(--ink-muted)" }}>
                    <span className="mono">IČO {r.ico}</span>
                    {r.address?.city && <span>·</span>}
                    {r.address?.street && <span className="truncate">{r.address.street}</span>}
                    {r.address?.city && <span>{r.address.city}</span>}
                  </div>
                </div>
                <span
                  className="text-popisek font-bold px-1.5 py-0.5 rounded uppercase tracking-wide flex-shrink-0"
                  style={{
                    background: r.is_person ? "#F3E8FF" : "var(--accent-tint)",
                    color: r.is_person ? "#7B3FA8" : "var(--accent)",
                  }}
                >
                  {r.is_person ? "OSVČ" : "Firma"}
                </span>
              </button>
            );
          })}
          <div className="px-2.5 py-1 text-drobny italic text-center" style={{ background: "#FAF7F3", color: "var(--ink-muted)" }}>
            Zdroj: ARES — Czech business registry
          </div>
        </div>
      )}
    </div>
  );
}
